import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import { Spinner } from "@src/components/ui/spinner";
import { LoginLayout } from "@src/pages/LoginLayout";
import { AlertTriangle } from "lucide-react";

interface LoginCallbackPageProps {
  error?: string | null;
  errorDescription?: string | null;
  isLoading?: boolean;
  onRetry?: () => void;
}

export const LoginCallbackPage = ({
  error,
  errorDescription,
  isLoading = false,
  onRetry,
}: LoginCallbackPageProps) => {
  const hasError = !!error;

  return (
    <LoginLayout>
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="md"
        className="w-full text-center"
      >
        {isLoading && !hasError ? (
          <>
            <Spinner size="lg" className="text-accent" />
            <HeaderText tag="h2">Completing sign in...</HeaderText>
            <Text sizing="sm" appearance="subdued" className="max-w-sm">
              Please wait while we verify your credentials.
            </Text>
          </>
        ) : null}

        {hasError ? (
          <>
            <VerticalFlexbox
              alignItems="center"
              justifyContent="center"
              className="size-12 rounded-full bg-destructive/10 text-destructive"
            >
              <AlertTriangle className="size-6" />
            </VerticalFlexbox>
            <HeaderText tag="h2">{getErrorTitle(error)}</HeaderText>
            <Text sizing="sm" appearance="subdued" className="max-w-sm">
              {formatErrorDescription(errorDescription, error)}
            </Text>
            {onRetry ? (
              <Button
                type="button"
                className="mt-1 bg-accent text-accent-foreground hover:bg-accent/90"
                onClick={onRetry}
              >
                Try again
              </Button>
            ) : null}
          </>
        ) : null}
      </VerticalFlexbox>
    </LoginLayout>
  );
};

function getErrorTitle(error: string | null | undefined): string {
  switch (error) {
    case "access_denied":
      return "Access Denied";
    case "invalid_request":
      return "Invalid Request";
    case "unauthorized_client":
      return "Unauthorized";
    case "unsupported_response_type":
      return "Configuration Error";
    case "invalid_scope":
      return "Invalid Scope";
    case "server_error":
      return "Server Error";
    case "temporarily_unavailable":
      return "Temporarily Unavailable";
    default:
      return "Authentication Failed";
  }
}

function formatErrorDescription(
  description: string | null | undefined,
  error: string | null | undefined,
): string {
  if (description) {
    return description;
  }

  switch (error) {
    case "access_denied":
      return "You do not have permission to access this application. Please contact your administrator.";
    case "invalid_request":
      return "The authentication request was invalid. Please try signing in again.";
    case "unauthorized_client":
      return "This application is not authorized to perform this action.";
    default:
      return "An error occurred during authentication. Please try signing in again.";
  }
}
